import React, { useState, useRef, useEffect } from 'react';

const RefundPolicy = () => {
  const [activeSection, setActiveSection] = useState(null);
  const sectionRefs = useRef({});

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    sectionRefs.current[sectionId]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    { id: 'overview', title: 'Overview' },
    { id: 'eligibility', title: '1. Eligibility for 100% Refund' },
    { id: 'request', title: '2. How to Request a Refund' },
    { id: 'timeline', title: '3. Refund Process Timeline' },
    { id: 'terms', title: '4. Refund Terms & Conditions' },
    { id: 'blacklisting', title: '5. Blacklisting Policy' },
    { id: 'contact', title: '6. Contact Us' }
  ];

  return (
    <div className="max-w-6xl mx-auto p-4 bg-white">
      {/* Header */}
      <div className="text-center mb-10">
        <h1 className="text-4xl font-bold text-blue-800 mb-2">Refund Policy</h1>
        <p className="text-xl text-gray-600">IndiaHirings.com</p>
        <p className="text-gray-500 mt-2">Effective Date: March 18, 2025</p>
      </div>

      {/* Introduction */}
      <div className="bg-blue-50 p-6 rounded-lg shadow-sm mb-8">
        <p className="text-gray-800 leading-relaxed">
          IndiaHirings.com ("we," "our," or "us") is committed to delivering the best value and results for your recruitment needs. Our 100% Money-Back Guarantee ensures a hassle-free refund if we fail to meet the agreed criteria.
        </p>
        <p className="text-gray-800 leading-relaxed mt-4 font-medium">
          Please read this Refund Policy carefully before using our services.
        </p>
      </div>

      {/* Table of Contents */}
      <div className="mb-10">
        <h2 className="text-2xl font-bold text-blue-800 mb-4">Table of Contents</h2>
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
            {sections.slice(1).map(section => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`text-left p-2 rounded-md transition-colors hover:bg-blue-50 ${activeSection === section.id ? 'bg-blue-100 text-blue-800' : 'text-gray-700'
                  }`}
              >
                {section.title}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content Sections */}
      <div className="space-y-10">
        {/* Section 1 */}
        <div
          ref={el => sectionRefs.current['eligibility'] = el}
          id="eligibility"
          className="scroll-mt-4"
        >
          <h2 className="text-2xl font-bold text-blue-800 mb-4">1. Eligibility for 100% Refund</h2>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <p className="text-gray-700 mb-4">
              You are eligible for a full refund if the following conditions are not met:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><span className="font-medium">Job Post Visibility:</span> Your job posting is not visible on the IndiaHirings platform within 24 hours of activation.</li>
              <li><span className="font-medium">Response Guarantee:</span> You do not receive at least 50% of the promised responses/applications within the plan duration.</li>
              <li><span className="font-medium">Relevant Applications:</span> You do not receive a minimum of 10 relevant candidate applications for your active job post within the first 7 days.</li>
              <li><span className="font-medium">Candidate Engagement:</span> Candidates do not respond to at least 20% of your outreach (calls, emails, or database access), as per plan features.</li>
              <li><span className="font-medium">Plan Activation Issues:</span> If there are technical issues from our side that prevent you from utilizing the plan benefits (e.g., inability to access the database or post jobs).</li>
              <li><span className="font-medium">Adherence to Guidelines:</span> Your job postings and employer profile meet the platform's terms of service and KYC verification requirements.</li>
            </ul>
          </div>
        </div>

        {/* Section 2 */}
        <div
          ref={el => sectionRefs.current['request'] = el}
          id="request"
          className="scroll-mt-4"
        >
          <h2 className="text-2xl font-bold text-blue-800 mb-4">2. How to Request a Refund?</h2>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <p className="text-gray-700 mb-4">
              If you believe the above criteria were not fulfilled, you can initiate a refund request through the following methods:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><span className="font-medium">Employer Dashboard:</span> Go to your dashboard, click on "Request Refund", and provide details about the issue.</li>
              <li><span className="font-medium">Email Support:</span> Send an email to <a href="mailto:refunds@indiahirings.com" className="text-blue-600 hover:underline">refunds@indiahirings.com</a> with the subject line: "Refund Request - [Your Plan Name]".</li>
              <li><span className="font-medium">Customer Support:</span> Call our support team at xxxxxxxx (Mon-Sat: 10 AM - 6 PM).</li>
              <li><span className="font-medium">Live Chat Support:</span> Use the Live Chat feature on our website or app to raise your refund request.</li>
            </ul>
          </div>
        </div>

        {/* Section 3 */}
        <div
          ref={el => sectionRefs.current['timeline'] = el}
          id="timeline"
          className="scroll-mt-4"
        >
          <h2 className="text-2xl font-bold text-blue-800 mb-4">3. Refund Process Timeline</h2>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><span className="font-medium">Initial Review:</span> Our team will validate your refund request within 48 to 72 hours of submission.</li>
              <li><span className="font-medium">Approval & Processing:</span> Once approved, the refund will be processed within 10 to 15 business days. The amount will be credited back to your original payment method.</li>
            </ul>
          </div>
        </div>

        {/* Section 4 */}
        <div
          ref={el => sectionRefs.current['terms'] = el}
          id="terms"
          className="scroll-mt-4"
        >
          <h2 className="text-2xl font-bold text-blue-800 mb-4">4. Refund Terms & Conditions</h2>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <p className="text-gray-700 mb-4">
              Refund requests must be submitted within 15 days of plan activation.
            </p>
            <p className="text-gray-700 mb-4">
              Refunds will not be issued if:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>The promised responses or applications have been delivered.</li>
              <li>Platform policies have been violated (e.g., fake job posts, misleading information, or blacklisted job types).</li>
              <li>The usage exceeds 25% of the plan benefits (e.g., database views, candidate calls, or job responses).</li>
            </ul>
            <p className="text-gray-700">
              Refunds are allowed only once per plan.
            </p>
          </div>
        </div>

        {/* Section 5 */}
        <div
          ref={el => sectionRefs.current['blacklisting'] = el}
          id="blacklisting"
          className="scroll-mt-4"
        >
          <h2 className="text-2xl font-bold text-blue-800 mb-4">5. Blacklisting Policy</h2>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <p className="text-gray-700 mb-4">
              Employers engaging in the following activities will be blacklisted and ineligible for refunds:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><span className="font-medium">Fake or Misleading Job Posts:</span> Posting incorrect job details or clickbait jobs.</li>
              <li><span className="font-medium">Charging Candidates:</span> Collecting fees like registration, placement, or exam fees.</li>
              <li><span className="font-medium">Policy Violations:</span> Jobs involving inappropriate content, MLM schemes, personal favors, or fraudulent activities.</li>
              <li><span className="font-medium">Misinformation:</span> Spreading false job details or misusing candidate information.</li>
            </ul>
          </div>
        </div>

        {/* Section 6 */}
        <div
          ref={el => sectionRefs.current['contact'] = el}
          id="contact"
          className="scroll-mt-4"
        >
          <h2 className="text-2xl font-bold text-blue-800 mb-4">6. Contact Us</h2>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <p className="text-gray-700 mb-4">
              If you have any questions, concerns, or requests regarding this Refund Policy, please contact us at:
            </p>
            <div className="bg-blue-50 p-4 rounded-md">
              <p className="font-medium text-blue-800">IndiaHirings.com</p>
              <p className="text-blue-800">refunds@indiaHirings.com</p>
              <p className="text-blue-800">Support: xxxxxxxx (Mon-Sat: 10 AM - 6 PM)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Note */}
      <div className="mt-12 bg-blue-50 p-6 rounded-lg shadow-sm">
        <h3 className="text-xl font-bold text-blue-800 mb-3">Why IndiaHirings.com?</h3>
        <p className="text-gray-700">
          We stand by our promise of quality and transparency. If we don't deliver as committed,
          we guarantee a 100% refund to ensure your satisfaction and trust.
        </p>
      </div>

      {/* Back to Top Button */}
      <div className="fixed bottom-6 right-6">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors"
          aria-label="Back to top"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default RefundPolicy;